import { useHighlightedTable } from '../utils';
import {
  type BaseExcelColor,
  ExcelColor,
  ExcelColorIndexed,
  TRANSPARENT,
} from '@/model/excelColor';
import { INDEXED_COLORS } from '@/model/workbookStyle';
import { Icon } from '@iconify/react';
import Chrome from '@uiw/react-color-chrome';
import type Color from 'colorjs.io';
import { Tooltip } from 'radix-ui';
import { DropdownMenu } from 'radix-ui';
import type React from 'react';
import { useState } from 'react';

function IndexedColors({
  colors,
  onChange,
}: {
  colors: (readonly [Color, number])[];
  onChange: (color: BaseExcelColor) => void;
}) {
  return colors.map(([c, i]) => (
    <DropdownMenu.Item
      key={i}
      className="flex cursor-pointer rounded-sm p-0 hover:ring-2 hover:ring-gray-400"
    >
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            className="h-8 w-8 cursor-pointer rounded-sm border-2 border-black/50"
            style={{ backgroundColor: c.toString({ type: 'hex' }) }}
            onClick={() => onChange(new ExcelColorIndexed(i))}
          />
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={5}
            side="bottom"
            className="pointer-events-none! rounded border border-gray-200 bg-white px-3.75 py-2.5 text-[15px] leading-none shadow will-change-[transform,opacity]"
          >
            <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-gray-200 border-r-transparent border-b-transparent bg-white"></span>
            {c.toString({ type: 'hex' })} ({i + 1})
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </DropdownMenu.Item>
  ));
}

export function ColorInput({
  color,
  onChange,
}: {
  color: BaseExcelColor | 'unset' | undefined | null;
  allowNull: true;
  onChange: (color: BaseExcelColor | 'unset' | null) => void;
}): React.ReactNode;
export function ColorInput({
  color,
  onChange,
}: {
  color: BaseExcelColor | 'unset' | undefined | null;
  onChange: (color: BaseExcelColor) => void;
}): React.ReactNode;
export function ColorInput({
  color,
  allowNull,
  onChange,
}: {
  color: BaseExcelColor | 'unset' | undefined | null;
  allowNull?: true;
  onChange:
    | ((color: BaseExcelColor) => void)
    | ((color: BaseExcelColor | 'unset' | null) => void);
}) {
  const [showAllIndexed, setShowAllIndexed] = useState(false);
  const [open, setOpen] = useState(false);
  const hexColor = color === 'unset' ? '#000' : (color?.toHex() ?? '#000');
  return (
    <div className="flex w-fit flex-col flex-wrap items-center gap-2 overflow-hidden rounded pt-2">
      <div
        className={`grid ${showAllIndexed ? 'grid-cols-8' : 'grid-cols-7'} gap-1 px-2`}
      >
        <IndexedColors
          colors={
            showAllIndexed
              ? INDEXED_COLORS.map((c, i) => [c, i] as const)
              : INDEXED_COLORS.map((c, i) => [c, i] as const).slice(2, 8)
          }
          onChange={onChange}
        />
        <div className="flex cursor-pointer rounded-sm p-0 hover:ring-2 hover:ring-gray-400">
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm"
                onClick={() => setShowAllIndexed(!showAllIndexed)}
              >
                {showAllIndexed ? (
                  <Icon icon="fluent:chevron-left-20-regular" />
                ) : (
                  <Icon icon="fluent:chevron-right-20-regular" />
                )}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                sideOffset={5}
                side="bottom"
                className="pointer-events-none! rounded border border-gray-200 bg-white px-3.75 py-2.5 text-[15px] leading-none shadow will-change-[transform,opacity]"
              >
                <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-gray-200 border-r-transparent border-b-transparent bg-white"></span>
                {showAllIndexed
                  ? 'Hide indexed colors'
                  : 'Show all indexed colors'}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </div>
      </div>
      <div className="flex w-full gap-1 px-2">
        <IndexedColors
          colors={
            showAllIndexed
              ? []
              : INDEXED_COLORS.map((c, i) => [c, i] as const).slice(0, 2)
          }
          onChange={onChange}
        />
        {allowNull && (
          <>
            <DropdownMenu.Item className="flex cursor-pointer rounded-sm p-0 hover:ring-2 hover:ring-gray-400">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    className="h-8 w-8 cursor-pointer rounded-sm border-[0.66rem] border-red-700 border-t-red-400 border-r-red-400 bg-red-200"
                    onClick={() => onChange('unset' as any)}
                  />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    sideOffset={5}
                    side="bottom"
                    className="pointer-events-none! rounded border border-gray-200 bg-white px-3.75 py-2.5 text-[15px] leading-none shadow will-change-[transform,opacity]"
                  >
                    <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-gray-200 border-r-transparent border-b-transparent bg-white"></span>
                    Unset
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </DropdownMenu.Item>
            <DropdownMenu.Item className="flex cursor-pointer rounded-sm p-0 hover:ring-2 hover:ring-gray-400">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    className="h-8 w-8 cursor-pointer rounded-sm border-[0.66rem] border-blue-700 border-t-blue-400 border-r-blue-400 bg-blue-200"
                    onClick={() => onChange(null as any)}
                  />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    sideOffset={5}
                    side="bottom"
                    className="pointer-events-none! rounded border border-gray-200 bg-white px-3.75 py-2.5 text-[15px] leading-none shadow will-change-[transform,opacity]"
                  >
                    <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-gray-200 border-r-transparent border-b-transparent bg-white"></span>
                    Use default
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </DropdownMenu.Item>
          </>
        )}
        <DropdownMenu.Item className="flex cursor-pointer rounded-sm p-0 hover:ring-2 hover:ring-gray-400">
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                className="h-8 w-8 cursor-pointer rounded-sm border-[0.66rem] border-t-gray-500 border-r-gray-500"
                onClick={() => onChange(TRANSPARENT)}
              />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                sideOffset={5}
                side="bottom"
                className="pointer-events-none! rounded border border-gray-200 bg-white px-3.75 py-2.5 text-[15px] leading-none shadow will-change-[transform,opacity]"
              >
                <span className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-gray-200 border-r-transparent border-b-transparent bg-white"></span>
                Transparent
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </DropdownMenu.Item>
      </div>
      {showAllIndexed ? (
        <div />
      ) : (
        <Chrome
          style={{
            borderInline: 'none',
            borderBottom: 'none',
            width: 'calc(var(--spacing) * 66)',
          }}
          color={hexColor}
          showTriangle={false}
          onChange={c => {
            onChange(ExcelColor.fromHex(c.hexa));
          }}
          onKeyDown={e => e.stopPropagation()}
        />
      )}
    </div>
  );
}
